import './NxComboBox.scss';

import * as React from 'react';

import { usePopper } from 'react-popper';

import { useMobile, useTimeout, useWindowSize } from '../../hooks';
import { translations } from '../../locale/translations';
import { classNameMerger, htmlEscape } from '../../utils/util';
import { FastClickContainer } from '../FastClickContainer/FastClickContainer';
import { NinoxPopup } from '../Popup/NinoxPopup';

export interface NxEditorItem<T> {
    /**
     * item id
     */
    id: T;
    /**
     * item caption
     */
    caption: string;
    /**
     * (optional) item icon
     */
    icon?: string;
    /**
     * (optional) item background color
     */
    color?: string;
    /**
     * (optional) item text color
     */
    textColor?: string;
    /**
     * sorting order
     * */
    order?: number;
}

const DROPDOWN_OFFSET = 6;

const BUTTONS = {
    UP: 38,
    DOWN: 40,
    ENTER: 13,
    ESCAPE: 27
};

export type NinoxComboBoxProps = {
    /**
     * Array of options where option is an object of type NxEditorItem
     */
    values: NxEditorItem<number | string>[];
    /**
     * Id of selected option
     * */
    selectedFieldId: number | string | null;
    /**
     * will be called with new selectedFieldId as an argument on selecting a field
     */
    onChange: (v: number | string | null) => void;
    /**
     * On desktop we have input in combobox
     * if disableInput is true it will be div like on mobile
     */
    disableInput?: boolean;
    /**
     * will disable an empty value
     * (does not add default value, use non null selectedFieldId for that)
     */
    required?: boolean;
    /**
     * disables the interactions while true (while updating)
     */
    updating?: boolean;
    /**
     * placeholder will be shown if empty
     */
    placeholder?: string;
    /**
     * base class from which all other classes are inherited
     */
    baseClass?: string;
    /**
     * class name of main container div or input regardless
     */
    className?: string;

    /**
     * this shouldn't be here but adding it to support translations on credentials-landing
     * should ideally be provided from a context provider
     */
    lang?: string;
};

export const NxComboBox = (props: NinoxComboBoxProps) => {
    const isMobile = useMobile();
    const disableInput = props.disableInput || isMobile;
    const locale = translations[props.lang || 'en'];
    const values = props.values;
    const [isOpen, setOpen] = React.useState(false);
    const [shouldShowSelected, setShouldShowSelected] = React.useState(false);
    const [searchString, setSearchString] = React.useState<string>();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const baseClass = props.baseClass ?? 'nx-combo-editor';

    const dropdownPopper = usePopper(inputRef.current, dropdownRef.current, {
        placement: 'bottom'
    });

    const timeout = useTimeout();
    const [winWidth, winHeight] = useWindowSize();
    const isEditMode = isOpen && !props.updating;

    const nullValue: NxEditorItem<number | string> = {
        id: -1,
        caption: locale.empty
    };

    const modelValue = values.find((a) => a.id === props.selectedFieldId) ?? nullValue;
    const [selected, setSelected] = React.useState(modelValue);

    const filteredValues = React.useMemo(() => {
        if (searchString) {
            const filtered = values.filter((el) =>
                (el.caption ?? el.id).toString().toLowerCase().includes(searchString.toLowerCase())
            );

            if (filtered.length && filtered.find((el) => el === selected) === undefined) {
                setSelected(filtered[0]);
            }

            return filtered;
        }

        if (!props.required) {
            return [nullValue, ...values];
        }

        return values;
    }, [searchString, values]);

    const stringValueSelected = selected.id !== nullValue.id ? selected.caption ?? selected.id : '';
    const stringValue = modelValue.id !== nullValue.id ? modelValue.caption ?? modelValue.id : '';
    const [popupStyles, setPopupStyles] = React.useState<React.CSSProperties>({});
    const [dropdownStyles, setDropdownStyles] = React.useState<React.CSSProperties>({});

    React.useLayoutEffect(() => {
        if (containerRef.current && isOpen) {
            const containerRect = containerRef.current.getBoundingClientRect();

            setPopupStyles({
                width: `${containerRect.width}px`,
                position: 'fixed',
                top: 0,
                left: 0,
                transform: `translate3d(${containerRect.left}px, ${containerRect.top}px, 0)`
            });

            const maxRoom = Math.max(winHeight - containerRect.bottom, containerRect.top);

            setDropdownStyles({
                minWidth: '100%',
                maxHeight: `${maxRoom - DROPDOWN_OFFSET * 2}px`,
                overflow: 'auto'
            });
        }
    }, [isOpen, winWidth, winHeight]);

    function updateSelected(val: NxEditorItem<number | string>) {
        setSelected(val);
        setShouldShowSelected(true);
    }

    function updateModel(val: NxEditorItem<number | string> | null) {
        props.onChange(val ? val.id : null);
    }

    function openBox() {
        if (!props.updating && !isOpen) {
            setSearchString(undefined);
            updateSelected(modelValue);

            if ((window as any).NINOX_IOS) {
                timeout(() => {
                    setOpen(true);
                }, 100);
            } else {
                setOpen(true);

                timeout(() => {
                    if (inputRef.current && inputRef.current.select) {
                        inputRef.current.select();
                    }
                }, 0);
            }
        }
    }

    function closeBox() {
        setOpen(false);
    }

    function cleanup(val?: NxEditorItem<number | string>) {
        if (!shouldShowSelected) {
            updateModel(val ?? filteredValues[0]);
        } else {
            updateModel(selected);
        }

        closeBox();
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        e.stopPropagation();
        const val = e.target.value;

        setShouldShowSelected(false);
        setSearchString(val);
    }

    function handleChoice(val: any) {
        updateModel(val.id === nullValue.id ? null : val);
        closeBox();
    }

    function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
        if (Object.values(BUTTONS).includes(e.which)) {
            e.preventDefault();
            e.stopPropagation();
            const l = filteredValues.length;
            const idx = filteredValues.findIndex((el: NxEditorItem<number | string>) => el.id === selected.id);

            switch (e.which) {
                case BUTTONS.UP:
                    if (idx === -1) {
                        updateSelected(filteredValues[l - 1]);
                    } else if (idx > 0) {
                        updateSelected(filteredValues[idx - 1]);
                    }

                    break;
                case BUTTONS.DOWN:
                    if (idx === -1) {
                        updateSelected(filteredValues[0]);
                    } else if (idx < l - 1) {
                        updateSelected(filteredValues[idx + 1]);
                    }

                    break;
                case BUTTONS.ESCAPE:
                    closeBox();
                    break;

                default: {
                    const newVal = idx !== -1 ? filteredValues[idx] : null;

                    if (newVal !== null || !props.required) {
                        updateModel(newVal);
                    }

                    closeBox();
                }
            }
        }
    }

    const setScrollTo = (index: number) => {
        if (dropdownRef.current) {
            dropdownRef.current.scrollTop = (dropdownRef.current.children[index] as HTMLDivElement)?.offsetTop;
        }
    };

    React.useEffect(() => {
        if (isOpen) {
            const currentIndex = props.values.findIndex((opt) => opt.id === props.selectedFieldId);

            setTimeout(() => setScrollTo(currentIndex), 0);
        }
    }, [isOpen, props.selectedFieldId]);

    return (
        <>
            <div
                ref={containerRef}
                className={classNameMerger(
                    baseClass,
                    props.className,
                    isEditMode ? `${baseClass}--open` : `${baseClass}--closed`,
                    props.updating ? `${baseClass}--updating` : ''
                )}
                style={{
                    backgroundColor: modelValue && modelValue.color
                }}
                onMouseDown={openBox}
                onTouchStart={openBox}
            >
                <div className={`${baseClass}__value`}>
                    {modelValue && modelValue.icon ? <div className={`ic ic-${modelValue.icon}`} /> : null}
                    <div className={!stringValue ? `${baseClass}__placeholder` : ''}>
                        {stringValue || props.placeholder}
                    </div>
                </div>
            </div>

            <NinoxPopup
                animationClass={`${baseClass}--animation`}
                animationDuration={0}
                className={`${baseClass}--animation`}
                handleClose={() => cleanup()}
                isVisible={isEditMode}
            >
                <div style={popupStyles}>
                    <input
                        ref={inputRef}
                        autoFocus
                        className={classNameMerger(
                            baseClass,
                            props.className,
                            `${baseClass}__popup`,
                            disableInput ? `${baseClass}--non-input` : `${baseClass}--input`,
                            'no-barcode'
                        )}
                        type="text"
                        value={shouldShowSelected ? stringValueSelected : searchString}
                        onChange={handleChange}
                        onKeyDown={handleKeyPress}
                    />
                    <div
                        ref={dropdownRef}
                        className={`${baseClass}__menu`}
                        style={{ ...dropdownPopper.styles.popper, ...dropdownStyles }}
                        {...dropdownPopper.attributes.popper}
                    >
                        {filteredValues.map((val: any) => (
                            <FastClickContainer
                                key={val.id}
                                className={classNameMerger(`${baseClass}__menu__item`, {
                                    selected: selected.id === val.id
                                })}
                                style={val.color ? { backgroundColor: val.color, color: val.textColor } : {}}
                                testID="nx-combo-box-select-option"
                                onFastClick={() => handleChoice(val)}
                            >
                                {val.icon ? <div className={`ic ic-${val.icon}`} /> : null}
                                <span> {htmlEscape(val.caption ?? val.id)} </span>
                                <div className={`${baseClass}__background`} />
                            </FastClickContainer>
                        ))}
                    </div>
                </div>
            </NinoxPopup>
        </>
    );
};
